import React from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useModules } from '../../context/ModuleContext';
import { Badge } from '../../design-system/Badge';
import { Button } from '../../design-system/Button';
import { BoardView } from './modules/BoardView';
import { ListView } from './modules/ListView';
import { CalendarView } from './modules/CalendarView';
import { TimelineView } from './modules/TimelineView';
import { SprintsView } from './modules/SprintsView';
import { DocsView } from './modules/DocsView';
import { MeetingsView } from './modules/MeetingsView';
import { AutomationsView } from './modules/AutomationsView';
import { ReportsView } from './modules/ReportsView';
import { GameDevView } from './modules/GameDevView';
import { HardwareView } from './modules/HardwareView';
import { ResearchView } from './modules/ResearchView';
import { ProductView } from './modules/ProductView';
import { CreativeView } from './modules/CreativeView';
import { Plus, Sliders, Globe, Lock } from 'lucide-react';

export const ProjectView = ({ currentProjectView, setCurrentView }) => {
  const { activeProject, setIsQuickAddOpen } = useWorkspace();
  const { modules } = useModules();

  const activeModuleMeta = modules.find((m) => m.id === currentProjectView);

  const renderModuleContent = () => {
    switch (currentProjectView) {
      case 'list':
        return <ListView />;
      case 'calendar':
        return <CalendarView />;
      case 'timeline':
        return <TimelineView />;
      case 'sprints':
        return <SprintsView />;
      case 'docs':
        return <DocsView />;
      case 'meetings':
        return <MeetingsView />;
      case 'automations':
        return <AutomationsView />;
      case 'reports':
        return <ReportsView />;
      case 'game_dev':
        return <GameDevView />;
      case 'hardware':
        return <HardwareView />;
      case 'research':
        return <ResearchView />;
      case 'product':
        return <ProductView />;
      case 'creative':
        return <CreativeView />;
      case 'board':
      default:
        return <BoardView />;
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Clean Project Header Banner without redundant top module tabs */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px',
          paddingBottom: '16px',
          borderBottom: '1px solid var(--border-divider)'
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
            <Badge variant="teal" size="sm">
              {activeProject?.key || 'PRJ'}
            </Badge>
            <h1 style={{ fontSize: '24px', fontWeight: '800' }}>{activeProject?.name}</h1>
            <Badge variant="recessed" size="xs">
              {activeProject?.visibility === 'Public' ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Globe size={11} color="var(--accent-teal)" /> Public
                </span>
              ) : (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                  <Lock size={11} /> Private
                </span>
              )}
            </Badge>
            {activeModuleMeta && (
              <Badge variant="neutral" size="xs" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '13px' }}>
                  {activeModuleMeta.icon}
                </span>
                {activeModuleMeta.name}
              </Badge>
            )}
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', maxWidth: '720px' }}>
            {activeProject?.description}
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            variant="secondary"
            size="sm"
            icon={<Sliders size={15} />}
            onClick={() => setCurrentView('module_library')}
          >
            Manage Modules
          </Button>
          <Button
            variant="primary"
            size="sm"
            icon={<Plus size={15} />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            New Task
          </Button>
        </div>
      </div>

      {/* Render Active View directly driven from sidebar */}
      <div>{renderModuleContent()}</div>
    </div>
  );
};
